import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Card } from "rebass";
import { css } from "styled-components";

import { getLeftSideWidth } from "../../../../store/modules/display/selectors";

import {
  CONTENT_ITEM_HEIGHT,
  CONTENT_MENU_HORIZONTAL_SHIFT,
  CONTENT_MENU_VERTICAL_SHIFT,
} from "./settings";

import { InstanceType } from "../../../../utils/Instance";
import { ActionType, ContentMenuPosition } from "./types";

import actionCollection from "./actionCollection";

import { ContentItem } from "./ContentItem";

interface Props {
  activeInstance: string | null;
  position: ContentMenuPosition | null;
}

const calcTop = (height: number, { currentTarget, offsetParent }: ContentMenuPosition) => (
  offsetParent.bottom > currentTarget.bottom + height + CONTENT_MENU_VERTICAL_SHIFT
    ? currentTarget.top + CONTENT_MENU_VERTICAL_SHIFT
    : currentTarget.bottom - (height + CONTENT_MENU_VERTICAL_SHIFT)
);

export function ContentMenu({ position, activeInstance }: Props) {
  const leftSideWidth = useSelector(getLeftSideWidth);

  const { id, type, items } = useMemo<{ id?: string, type?: InstanceType, items: Array<ActionType> }>(() => {
    if (activeInstance === null) {
      return ({ items: [] });
    }
    const [activeId, activeType] = activeInstance.split(",");

    return ({
      id: activeId,
      type: activeType as InstanceType,
      items: actionCollection[activeType as InstanceType],
    });
  }, [activeInstance]);

  const itemCounts = items.length;

  const cssProp = useMemo(() => {
    const height = itemCounts * CONTENT_ITEM_HEIGHT;

    return (
      css`
        position: fixed;
        height: ${height}px;
        left: ${leftSideWidth}%;
        z-index: 3;
        transition: all .4s ease-in-out;
        overflow: hidden;
        transform: translateX(-${CONTENT_MENU_HORIZONTAL_SHIFT}px);
        top: ${position ? calcTop(height, position) : 0}px;
      `
    );
  }, [position, leftSideWidth, itemCounts]);

  if (!id || !type || !items) {
    return null;
  }

  return (position && (
    <Card
      width="325px"
      borderRadius="6px"
      border="solid 1px #e7e7e7"
      backgroundColor="#fff"
      boxShadow="0 26px 24px 0 rgba(0,0,0,.21)"
      css={cssProp}
    >
      {items.map(key => (
        <ContentItem
          key={key}
          instance={{ id, type }}
          actionType={key}
        />
      ))}
    </Card>
  ));
}
